import { Trophy, Medal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Participant } from '@/api/challenges';
import { formatNumber } from '@/utils/formatters';

interface LeaderboardProps {
  participants: Participant[];
  currentUserId?: string;
}

export function Leaderboard({ participants, currentUserId }: LeaderboardProps) {
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-700" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-gray-500">{rank}</span>;
    }
  };

  const sortedParticipants = [...participants].sort((a, b) => (b.totalRunDistance ?? 0) - (a.totalRunDistance ?? 0));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Trophy className="w-5 h-5 text-green-600" />
          <span>Leaderboard</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sortedParticipants.map((participant, index) => {
            const rank = index + 1;
            const isCurrentUser = participant.user?.id === currentUserId;
            const displayName = participant.user?.username || participant.walletAddress.slice(0, 6) + '...';
            const progress = Math.round(((participant.daysCompleted ?? 0) / 21) * 100); // Assuming 21 day challenge

            return (
              <div
                key={participant.walletAddress}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  isCurrentUser ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3 flex-1">
                  <div className="flex-shrink-0">{getRankIcon(rank)}</div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isCurrentUser ? 'text-green-900' : 'text-gray-900'}`}>
                      {displayName}
                      {isCurrentUser && <span className="ml-2 text-xs text-green-600">(You)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{formatNumber(participant.totalRunDistance ?? 0)} km</p>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-gray-900">{progress}%</span>
                  </div>
                  <div className="w-20 bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        isCurrentUser ? 'bg-green-600' : 'bg-green-500'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {sortedParticipants.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No participants yet</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
